import {
  List,
  Typography,
} from '@mui/material';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ExpertSuggestion } from 'FRONTEND/components/Calendar/AppointmentForm/ExpertSuggestion';
import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import Loader from 'FRONTEND/components/Loader';
import { pickExpert } from 'FRONTEND/reducers/bookingSlice';
import {
  searchRequest as expertSearchRequest,
  selectManagedExpertById,
} from 'FRONTEND/reducers/expertSlice';
import {
  selectAllMatchingExperts,
  selectSlotAvailableExperts,
} from 'FRONTEND/selectors/expertSelectors';
import { FormTypes } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getInitials } from 'FRONTEND/utils/initials';
import { getDurationSettingKey } from 'FRONTEND/utils/stringFormatting';

export const AvailableExpertStep = ({
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const selectedExpert = useSelector((state) => selectManagedExpertById(state, state.booking.selectedExpertId));
  const isLocationForm = useSelector((state) => state.booking.formType === FormTypes.LOCATION_FORM);
  const matchingExpertList = useSelector(isLocationForm ? selectSlotAvailableExperts : selectAllMatchingExperts);
  const selectedType = useSelector((state) => state.booking.selectedType);
  const isLoading = useSelector((state) => state.app.requestCounter[expertSearchRequest.toString()] > 0);
  const isSmall = useSelector((state) => state.app.isSmall);

  const tenantList = useSelector((state) => state.user.tenantList);
  const selectedLocation = useSelector((state) => state.booking.selectedLocationId ? state.location.entities[state.booking.selectedLocationId] : null);
  const isLocationFormPremiocareTenant = isLocationForm &&
    Boolean(selectedLocation?.tenantId && tenantList[selectedLocation.tenantId]?.premiocare_cabinet_id);

  useEffect(() => {
    if (shouldReset) {
      dispatch(pickExpert({ id: null }));
    }
  }, [shouldReset]);

  const handleExpertChange = (id) => {
    dispatch(pickExpert({ id: id || null }));
    onStepCompleted();
  };

  const listClasses = cx('appointmentForm-resultList', { 'appointmentForm-resultList_small': isSmall });

  return (
    <StepTemplate
      label={i18n._('Select a practitioner')}
      isLocked={isLocked}
      onEditClick={() => onStepClick()}
      isStepCompleted={selectedExpert && isCompleted}
      editText={selectedExpert?.alias || ''}
      alerts={[
        { condition: isLocationFormPremiocareTenant, text: i18n._('The practitioner\'s RPPS number must be set on both Parsys and Premiocare systems to make an appointment.'), severity: 'info' },
        { condition: isLocationFormPremiocareTenant && selectedExpert && !selectedExpert.prNumber, text: i18n._('This practitioner\'s RPPS number is not set in Parsys.'), severity: 'warning' },
      ]}
      {...otherProps}
    >
      <Loader isLoading={isLoading} position='verticalFlow' />
      {matchingExpertList.length ? (
        <List dense className={listClasses}>
          {matchingExpertList.map((matchingExpert) => {
            return (
              <ExpertSuggestion
                key={matchingExpert?.id}
                onClick={() => handleExpertChange(matchingExpert.id)}
                specialty={matchingExpert.primary_specialty}
                displayName={matchingExpert.alias}
                initials={getInitials(matchingExpert.alias)}
                picture={matchingExpert.picture}
                email={matchingExpert.login}
                duration={!isLocationForm && matchingExpert[getDurationSettingKey(selectedType)]}
                className='appointmentForm-expertSuggestion'
              />
            );
          })}
        </List>
      ) : (
        <Typography>{i18n._('No expert is available for this slot or this type of appointment.')}</Typography>
      )}
    </StepTemplate>
  );
};
